import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../utils/api";
import { commentNotificationsReadEnabled } from "../utils/commentNotificationsReadEnabled";
import { pollingRefetchInterval } from "../utils/requestFailure";

/** Root key of the shared unread counts; invalidating it refreshes every consumer. */
export const UNREAD_COMMENTS_QUERY_ROOT_KEY = ["dashboard", "unread-comments-count"];
/** Window event that asks every unread-count consumer to refresh now. */
const REFRESH_UNREAD_COMMENTS_COUNT_EVENT = "refreshUnreadCommentsCount";
const UNREAD_COMMENTS_POLL_MS = 30_000;

/** `GET /api/v1/conversations/unread-counts` as the consumers read it. */
export interface UnreadCountsDetails {
  total?: unknown;
  mentioned_unread_total?: unknown;
  by_reason?: Record<string, unknown> | null;
  latest_mention_notification?: {
    conversation_id?: string | number | null;
    comment_id?: string | number | null;
    reason?: string | null;
  } | null;
}

export interface ConversationUnreadCounts {
  /** Unread conversations shown on the nav and filter badges. */
  total: number;
  /** The full per-reason payload; null on the legacy count route. */
  details: UnreadCountsDetails | null;
}

function toCount(value: unknown) {
  const count = Number(value ?? 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

async function fetchConversationUnreadCounts(
  chatbotIdParam: string,
  notificationsEnabled: boolean,
): Promise<ConversationUnreadCounts> {
  const params = { chatbot_id: chatbotIdParam };
  if (notificationsEnabled) {
    const response = await api.get("/api/v1/conversations/unread-counts", { params });
    const details = (response.data ?? {}) as UnreadCountsDetails;
    return { total: toCount(details.total), details };
  }
  const response = await api.get("/api/v1/conversations/unread-comments-count", { params });
  return { total: toCount(response.data?.unread_comments_count), details: null };
}

/**
 * Unread comment counts for `chatbotIds`, shared by the nav badge, the mention
 * toasts and the Conversations filter: consumers with the same chatbot scope
 * share one cache entry, so polling observers collapse into one request per
 * interval. `poll` refreshes every 30 s while the tab is visible; focus
 * refetches when a hidden tab comes back. A 401/403/404 stops the poll until
 * such a refetch succeeds.
 */
export function useConversationUnreadCounts({
  chatbotIds,
  enabled,
  poll = false,
}: {
  chatbotIds: string[];
  enabled: boolean;
  poll?: boolean;
}) {
  const queryClient = useQueryClient();
  const notificationsEnabled = commentNotificationsReadEnabled();
  const chatbotIdParam = chatbotIds.join(",");
  const chatbotIdsKey = useMemo(() => [...chatbotIds].sort().join(","), [chatbotIds]);
  const isEnabled = enabled && chatbotIdParam.length > 0;

  useEffect(() => {
    if (!isEnabled) return undefined;
    // Every consumer listens, but joining the running refetch keeps it to one request.
    const onRefresh = () => {
      void queryClient.invalidateQueries(
        { queryKey: UNREAD_COMMENTS_QUERY_ROOT_KEY },
        { cancelRefetch: false },
      );
    };
    window.addEventListener(REFRESH_UNREAD_COMMENTS_COUNT_EVENT, onRefresh);
    return () => window.removeEventListener(REFRESH_UNREAD_COMMENTS_COUNT_EVENT, onRefresh);
  }, [isEnabled, queryClient]);

  return useQuery({
    queryKey: [
      ...UNREAD_COMMENTS_QUERY_ROOT_KEY,
      chatbotIdsKey,
      notificationsEnabled ? "notifications-v2" : "legacy",
    ],
    queryFn: () => fetchConversationUnreadCounts(chatbotIdParam, notificationsEnabled),
    enabled: isEnabled,
    staleTime: 1_000,
    // The next poll retries; retrying now only adds load while the API is struggling.
    retry: false,
    refetchInterval: (query) =>
      pollingRefetchInterval(query, poll ? UNREAD_COMMENTS_POLL_MS : false),
  });
}
